import { Helmet } from 'react-helmet-async'

const SITE_URL = 'https://prismscope.ai'
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`

/**
 * Per-route SEO tags — the single source of truth for the document title
 * and all crawl/social metadata.
 *
 * index.html is a pure shell (no title/description/og/twitter/canonical):
 * keeping those static AND injecting them here would duplicate every tag
 * in the prerendered output, since react-helmet-async appends rather than
 * replaces. Every routed page must render a <Seo>.
 *
 * Props:
 *   title       — page title (required). Rendered as "<title> | PrismScope"
 *                 unless `rawTitle` is set.
 *   description — meta description (required, aim for 150-160 chars)
 *   path        — route path starting with "/" (e.g. "/pricing"). Used for
 *                 the canonical URL and og:url. Defaults to "/".
 *   image       — absolute OG image URL (optional, defaults to /og-image.png)
 *   rawTitle    — if true, use `title` verbatim without the "| PrismScope" suffix
 *   noindex     — if true, emit a robots noindex tag
 */
export default function Seo({
  title,
  description,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  rawTitle = false,
  noindex = false,
}) {
  const fullTitle = rawTitle ? title : `${title} | PrismScope`
  const canonical = `${SITE_URL}${path}`

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="PrismScope" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={fullTitle} />
    </Helmet>
  )
}
